import { CommonModule, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, TemplateRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { DrakeSharedService } from 'app/services/drake-shared.service';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { PageService } from 'app/services/page.service';
import { SharedService } from 'app/services/shared.service';
import { Subject, takeUntil, forkJoin, of, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { DrakeToolbarComponent } from 'app/drake-components/drake-toolbar/drake-toolbar.component';
import { ConfigService } from 'app/services/config.service';
import { environment } from 'environments/environment';
import { HelpToolbarComponent } from 'app/drake-components/help-toolbar/help-toolbar.component';
import { GridService } from 'app/services/grid.service';
import { FileService } from '../../../../services/file.service';

interface ToolbarCompanyOption {
    text: string;
    value: string;
    isPro?: boolean;
    proPlan?: string;
    partitaIva?: string;
    colors?: { text: string, bg: string };
}

interface SelectedCompanyDetails {
    idAzienda: string;
    PartitaIva: string;
    RagioneSociale: string;
    isSubscribed?: boolean;
    subscriptionPlan?: string;
}

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    //styleUrls: ['./classy.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [CommonModule, FuseLoadingBarComponent, FuseVerticalNavigationComponent, NotificationsComponent, UserComponent, NgIf, MatIconModule, MatButtonModule, MatFormFieldModule, MatSelectModule, MatDialogModule, FormsModule, LanguagesComponent, FuseFullscreenComponent, SearchComponent, ShortcutsComponent, MessagesComponent, RouterOutlet, QuickChatComponent, DrakeToolbarComponent, HelpToolbarComponent],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    logoUrl: string;
    logoUrl2: string;
    public drakeName: string = environment.drakeName;
    public drakeVersion: string = environment.drakeVersion;
    public drakeHelp: boolean = environment.modules.help;
    public modules: any = environment.modules;
    public showDropdownToolbar: boolean = (environment as any).dropdownToolbar === true;
    public toolbarCompanies: ToolbarCompanyOption[] = [];
    public companySelected: string = localStorage.getItem('companySelected') ?? '';
    public companyIsPro: boolean = false;
    public subscribeCompanyUserId: string = '';

    public searchTerm: string = '';
    public sortBy: 'default' | 'alphabetical' | 'pro' = 'default';
    private _dialogRef: any;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        public drakeSharedService: DrakeSharedService,
        private configService: ConfigService,
        private gridService: GridService,
        private _dialog: MatDialog,
        private fileService: FileService
    ) {

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.loadBranding(); // Passa l'ID corretto
        this.loadToolbarCompanies();

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe((takeUntil(this._unsubscribeAll)))
            .subscribe((user: User) => {
                this.user = user;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }
    loadBranding(): void {
        const branding = localStorage.getItem('branding');
        if (branding) {
            const config = JSON.parse(branding);
            this.logoUrl = config.brdLogo1;
        }


        const user = localStorage.getItem('user');
        if (user) {
            const userconfig = JSON.parse(user);
            if (userconfig.extraData?.CustomLogo) {
                this.fileService.readFileAsBase64String(userconfig.extraData?.CustomLogo).subscribe((result: any) => {
                    this.logoUrl2 = result.dataUrl;
                });
            }
        }
    }

    loadToolbarCompanies(): void {
        if (!this.showDropdownToolbar) {
            return;
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user?.id;

        if (!userId) {
            return;
        }

        const requestData = {
            ViewCode: 'AZIENDEUTENTI.DEFAULT',
            Filter: JSON.stringify({}),
            Sort: '{}',
            Page: 0,
            PageSize: 0,
            Language: '',
            Cache: false,
            ExtraData: {}
        };

        this.gridService.getScopeData('AZIENDEUTENTI', 'READ', requestData)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (result) => {
                    const studioCompany: ToolbarCompanyOption = {
                        text: 'Il tuo studio',
                        value: `STUDIO`,
                        isPro: false,
                        partitaIva: '',
                        proPlan: '',
                        colors: { text: '', bg: '' }
                    };

                    const rawCompanyOptions = (result?.data ?? [])
                        .map((company: any) => ({
                            text: this.getToolbarCompanyText(company),
                            value: this.getToolbarCompanyValue(company?.idAzienda),
                            isPro: false,
                            // Aggiungiamo il controllo dentro l'oggetto idAzienda!
                            partitaIva: company?.PIva ??
                                company?.PartitaIva ??
                                company?.idAzienda?.PIva ??
                                company?.idAzienda?.PartitaIva ??
                                ''
                        }));

                    const companyOptionsMap = new Map();
                    rawCompanyOptions.forEach((option: any) => {
                        const key = option.partitaIva || option.value;
                        if (!companyOptionsMap.has(key)) {
                            companyOptionsMap.set(key, option);
                        }
                    });

                    const companyOptions = Array.from(companyOptionsMap.values())
                        .sort((a: any, b: any) => (a.text || '').localeCompare(b.text || ''));

                    const initialCompanies = [studioCompany, ...companyOptions];

                    // Fetch subscriptions in parallel for all loaded companies
                    const enrichmentObs = initialCompanies.map(company =>
                        forkJoin({
                            subscriptions: this.getCompanySubscriptionObservable(company.value),
                            partitaIva: this.getCompanyPivaObservable(company.value)
                        }).pipe(
                            // Uniamo i dati scaricati all'oggetto dell'azienda
                            map(data => {
                                const activeSubscription = (data.subscriptions || []).find((subscription: any) =>
                                    this.isSubscriptionActive(subscription?.abbonamentoAttivo)
                                );
                                const isPro = !!activeSubscription;
                                const proPlan = activeSubscription ? this.getSubscriptionPlanName(activeSubscription?.nomeAbbonamento) : undefined;
                                const colors = activeSubscription ? this.getSubscriptionPlanColors(activeSubscription?.nomeAbbonamento) : null;
                                
                                return { ...company, isPro, partitaIva: data.partitaIva, proPlan, colors };
                            })
                        )
                    );
                    forkJoin(enrichmentObs).pipe(takeUntil(this._unsubscribeAll)).subscribe({
                        next: (updatedCompanies) => {
                            this.toolbarCompanies = updatedCompanies;
                            console.log('updatedCompanies', updatedCompanies);

                            if (this.toolbarCompanies.length === 2) {
                                this.companySelected = this.toolbarCompanies[1].value;

                                this.selectAndSaveCompanyDetails(this.companySelected);
                                this.loadCompanySubscription(this.companySelected);

                                return;
                            }

                            if (!this.toolbarCompanies.length) {
                                return;
                            }

                            // Recuperiamo il vecchio valore dal localStorage. 
                            const storedCompanyRaw = localStorage.getItem('companySelected');
                            let storedCompanyId = '';
                            if (storedCompanyRaw) {
                                try {
                                    const parsed = JSON.parse(storedCompanyRaw);
                                    storedCompanyId = parsed?.idAzienda ?? storedCompanyRaw;
                                } catch (e) {
                                    storedCompanyId = storedCompanyRaw;
                                }
                            }

                            const storedCompanyExists = this.toolbarCompanies.some((company) => company.value === storedCompanyId);
                            this.companySelected = storedCompanyExists ? storedCompanyId : this.toolbarCompanies[0].value;

                            // Carichiamo dettagli e abbonamento per l'azienda selezionata
                            this.selectAndSaveCompanyDetails(this.companySelected);
                            this.loadCompanySubscription(this.companySelected);
                        },
                        error: (error) => {
                            console.error('Errore durante il caricamento degli abbonamenti toolbar:', error);
                            this.toolbarCompanies = initialCompanies;
                        }
                    });
                },
                error: (error) => {
                    console.error('Errore durante il caricamento delle aziende toolbar:', error);
                }
            });
    }

    getToolbarCompanyText(company: any): string {
        if (typeof company?.idAzienda === 'object' && company.idAzienda.text !== null) {
            return String(company.idAzienda.text ?? '');
        }

        return String(company?.Title ?? '');
    }

    truncateToolbarCompanyText(text: string): string {
        const words = text.trim().split(/\s+/).filter((word) => word);

        if (words.length <= 3) {
            return text;
        }

        return `${words.slice(0, 3).join(' ')} ....`;
    }

    getToolbarCompanyValue(idAzienda: any): string {
        if (typeof idAzienda === 'object' && idAzienda !== null) {
            return String(idAzienda._id ?? idAzienda.id ?? idAzienda.value ?? idAzienda.Value ?? '');
        }

        return String(idAzienda ?? '');
    }

    updateLocalStorageCompany(idAzienda: string, data: Partial<SelectedCompanyDetails>): void {
        const storedCompanyRaw = localStorage.getItem('companySelected');
        let currentDetails: Partial<SelectedCompanyDetails> = {};

        if (storedCompanyRaw) {
            try {
                currentDetails = JSON.parse(storedCompanyRaw);
            } catch (e) {
                // Retrocompatibilità nel caso ci sia solo l'ID come stringa
                currentDetails = { idAzienda: storedCompanyRaw };
            }
        }

        // Se l'ID in storage è diverso da quello che stiamo salvando, resettiamo l'oggetto
        if (currentDetails.idAzienda !== idAzienda) {
            currentDetails = { idAzienda };
        }

        // Uniamo i dati vecchi con quelli nuovi passati
        const newDetails = { ...currentDetails, ...data };
        localStorage.setItem('companySelected', JSON.stringify(newDetails));
    }

    onToolbarCompanyChange(idAzienda: string): void {
        this.companySelected = idAzienda;
        this.selectAndSaveCompanyDetails(idAzienda);
        this.loadCompanySubscription(idAzienda);
    }

    selectAndSaveCompanyDetails(idAzienda: string): void {
        if (idAzienda === 'STUDIO') {
            this.updateLocalStorageCompany(idAzienda, {
                PartitaIva: '',
                RagioneSociale: 'Il tuo studio'
            });
            return;
        }

        const requestData = {
            ViewCode: 'AZIENDE.DEFAULT',
            Filter: JSON.stringify({ _id: idAzienda }),
            Sort: '{}',
            Page: 0,
            PageSize: 1,
            Language: '',
            Cache: false,
            ExtraData: {}
        };

        this.gridService.getScopeData('AZIENDE', 'READ', requestData)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (result) => {
                    const aziendaData = (result?.data ?? [])[0];

                    if (aziendaData) {
                        this.updateLocalStorageCompany(idAzienda, {
                            PartitaIva: aziendaData.PIva ?? '',
                            RagioneSociale: aziendaData.RagioneSociale ?? ''
                        });
                    } else {
                        this.updateLocalStorageCompany(idAzienda, {
                            PartitaIva: '',
                            RagioneSociale: ''
                        });
                    }
                },
                error: (error) => {
                    console.error('Errore durante il recupero dei dettagli azienda da AZIENDE:', error);
                }
            });
    }

    getCompanyPivaObservable(idAzienda: string): Observable<string> {
        if (!this.showDropdownToolbar || !idAzienda || idAzienda === 'STUDIO') {
            return of('');
        }

        const requestData = {
            ViewCode: 'AZIENDE.DEFAULT',
            Filter: JSON.stringify({ _id: idAzienda }),
            Sort: '{}',
            Page: 0,
            PageSize: 1,
            Language: '',
            Cache: false,
            ExtraData: {}
        };

        return this.gridService.getScopeData('AZIENDE', 'READ', requestData).pipe(
            map((result) => {
                const aziendaData = (result?.data ?? [])[0];
                // Recupera la P.IVA usando gli stessi campi che usi per il localStorage
                return aziendaData?.PIva ?? aziendaData?.PartitaIva ?? '';
            }),
            catchError((error) => {
                console.error(`Errore nel caricamento della P.IVA per l'azienda ${idAzienda}:`, error);
                return of('');
            })
        );
    }

    loadCompanySubscription(idAzienda: string): void {
        this.companyIsPro = false;
        this.subscribeCompanyUserId = '';

        if (!this.showDropdownToolbar || !idAzienda) {
            return;
        }

        const selectedCompany = idAzienda;
        const currentUserId = this.getCurrentUserId();
        const isStudio = idAzienda === `STUDIO`;

        const requestData = {
            ViewCode: 'ABBONAMENTIAZIENDA.DEFAULT',
            Filter: JSON.stringify(isStudio ? { idUtente: currentUserId, SubType: 'STUDIO' } : { idAzienda }),
            Sort: '{}',
            Page: 0,
            PageSize: 0,
            Language: '',
            Cache: false,
            ExtraData: {}
        };

        this.gridService.getScopeData('ABBONAMENTIAZIENDA', 'READ', requestData)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (result) => {
                    if (selectedCompany !== this.companySelected) {
                        return;
                    }

                    const subscriptions = result?.data ?? [];
                    const activeSubscription = subscriptions.find((subscription: any) =>
                        this.isSubscriptionActive(subscription?.abbonamentoAttivo)
                    );
                    this.companyIsPro = !!activeSubscription;
                    const subscriptionPlan = this.getSubscriptionPlanName(activeSubscription?.nomeAbbonamento);

                    this.updateLocalStorageCompany(idAzienda, {
                        isSubscribed: this.companyIsPro,
                        subscriptionPlan: activeSubscription?.nomeAbbonamento.value
                    });

                    if (this.companyIsPro) {
                        return;
                    }

                    if (isStudio) {
                        this.subscribeCompanyUserId = subscriptions.length ? String(subscriptions[0]?._id ?? '') : selectedCompany;
                        return;
                    }

                    const subscription = subscriptions.find((item: any) => this.getRecordIdValue(item?.idAzienda) === idAzienda) ?? subscriptions[0];
                    const subscriptionCompanyId = this.getRecordIdValue(subscription?.idAzienda) || idAzienda;
                    const subscriptionUserId = this.getRecordIdValue(subscription?.idUtente) || currentUserId;

                    this.loadSubscribeCompanyUserId(subscriptionCompanyId, subscriptionUserId, selectedCompany);
                },
                error: (error) => {
                    this.companyIsPro = false;
                    this.subscribeCompanyUserId = '';

                    this.updateLocalStorageCompany(idAzienda, {
                        isSubscribed: false,
                        subscriptionPlan: 'NOPLAN'
                    });

                    console.error('Errore durante il caricamento degli abbonamenti azienda:', error);
                }
            });
    }

    loadSubscribeCompanyUserId(idAzienda: string, idUtente: string, selectedCompany: string): void {
        if (!idAzienda || !idUtente) {
            return;
        }

        const requestData = {
            ViewCode: 'AZIENDEUTENTI.DEFAULT',
            Filter: JSON.stringify({ idAzienda }),
            Sort: '{}',
            Page: 0,
            PageSize: 0,
            Language: '',
            Cache: false,
            ExtraData: {}
        };

        this.gridService.getScopeData('AZIENDEUTENTI', 'READ', requestData)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (result) => {
                    if (selectedCompany !== this.companySelected) {
                        return;
                    }

                    this.subscribeCompanyUserId = String((result?.data ?? [])[0]?._id ?? '');
                },
                error: (error) => {
                    this.subscribeCompanyUserId = '';
                    console.error('Errore durante il caricamento dell azienda utente:', error);
                }
            });
    }

    isSubscriptionActive(value: any): boolean {
        return value === true || value === 'true' || value === 1 || value === '1';
    }

    getSubscriptionPlanName(value: any): string {
        if (value === null || value === undefined) {
            return 'NOPLAN';
        }

        if (typeof value === 'string' || typeof value === 'number') {
            const planName = String(value).trim();
            return planName && planName !== '#N/A#' ? planName : 'NOPLAN';
        }

        if (typeof value === 'object') {
            const candidates = [
                value.attribute4,
                value.text,
                value.value
            ];

            for (const candidate of candidates) {
                const planName = this.getSubscriptionPlanName(candidate);

                if (planName !== 'NOPLAN') {
                    return planName;
                }
            }
        }

        return 'NOPLAN';
    }

    getSubscriptionPlanColors(value: any): { text: string, bg: string } | null {

        if (value === null || value === undefined) {
            return null;
        }

        if (typeof value === 'string' || typeof value === 'number') {
            const planName = String(value).trim();
            return null;
        }

        return { text: value.attribute1, bg: value.attribute2 };
    }

    getRecordIdValue(value: any): string {
        if (typeof value === 'object' && value !== null) {
            return String(value._id ?? value.id ?? value.value ?? value.Value ?? '');
        }

        return String(value ?? '');
    }

    getCurrentUserId(): string {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return String(user?.id ?? '');
    }

    goToSubscribe(): void {
        if (!this.subscribeCompanyUserId) {
            return;
        }

        this._router.navigate(['/p', 'ABBONATI', this.subscribeCompanyUserId]);
    }

    goHome() {
        this._router.navigate(['/'])
    }
    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    getCompanySubscriptionObservable(idAzienda: string): Observable<any[]> {
        if (!this.showDropdownToolbar || !idAzienda) {
            return of([]);
        }

        const currentUserId = this.getCurrentUserId();
        const isStudio = idAzienda === `STUDIO`;

        const requestData = {
            ViewCode: 'ABBONAMENTIAZIENDA.DEFAULT',
            Filter: JSON.stringify(isStudio ? { idUtente: currentUserId, SubType: 'STUDIO' } : { idAzienda }),
            Sort: '{}',
            Page: 0,
            PageSize: 0,
            Language: '',
            Cache: false,
            ExtraData: {}
        };

        return this.gridService.getScopeData('ABBONAMENTIAZIENDA', 'READ', requestData).pipe(
            map((result) => {
                const subscriptions = result?.data ?? [];
                return subscriptions;
            }),
            catchError((error) => {
                console.error(`Errore nel caricamento dell'abbonamento per l'azienda ${idAzienda}:`, error);
                return of([]);
            })
        );
    }

    openCompanySelectorModal(templateRef: TemplateRef<any>): void {
        this.searchTerm = '';
        this.sortBy = 'default';
        this._dialogRef = this._dialog.open(templateRef, {
            width: '500px',
            autoFocus: false,
            panelClass: 'drake-company-selector-dialog'
        });
    }

    closeCompanySelectorModal(): void {
        if (this._dialogRef) {
            this._dialogRef.close();
        }
    }

    selectCompanyFromModal(idAzienda: string): void {
        this.onToolbarCompanyChange(idAzienda);
        this.closeCompanySelectorModal();
    }

    getSelectedCompanyText(): string {

        const selected = this.toolbarCompanies.find(c => c.value === this.companySelected);
        return selected ? selected.text : '';
    }
    getSelectedCompanyPlan(): string {
        const selected = this.toolbarCompanies.find(c => c.value === this.companySelected);
        return selected ? selected.proPlan : '';
    }
    getSelectedCompanyColors(): { text: string, bg: string } | null {
        const selected = this.toolbarCompanies.find(c => c.value === this.companySelected);
        return selected ? selected.colors : null;
    }

    get filteredCompanies(): ToolbarCompanyOption[] {
        const term = this.searchTerm.toLowerCase().trim();
        if (!term) {
            return this.toolbarCompanies;
        }
        return this.toolbarCompanies.filter(company =>
            company.text.toLowerCase().includes(term)
        );
    }

    get sortedAndFilteredCompanies(): ToolbarCompanyOption[] {

        let list = [...this.filteredCompanies];

        if (this.sortBy === 'alphabetical') {
            list.sort((a, b) => a.text.localeCompare(b.text));
        } else if (this.sortBy === 'pro') {
            list.sort((a, b) => {
                if (a.isPro && !b.isPro) return -1;
                if (!a.isPro && b.isPro) return 1;
                return a.text.localeCompare(b.text);
            });
        }
        return list;
    }

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
}
